<script setup lang="ts">
import { computed } from 'vue';

type SizeNumber = 1 | 2 | 3 | 4 | 5 | 6;

type SizeString = `${SizeNumber}`;

type Size = SizeNumber | SizeString;

type Image = {
    url: string;
    alt: string;
    width?: string;
    height?: string;
};

// This component is used to display and empty state for example: a component, section or page. The
// size prop decides the font size and the emphasis prop sets how important the text is to the user.
const { size = '4', emphasis = 'normal', image = undefined } = defineProps<{
    /** The image properties */
    image?: Image;
    /** The text to display */
    text: string;
    /** The size for text */
    size?: Size;
    /** The emphasis for the text */
    emphasis?: 'subtle' | 'normal' | 'strong';
}>();

const weight = computed<'light' | 'lighter' | 'normal' | 'medium' | 'bold' | 'bolder'>(() => {
    switch (emphasis) {
        case 'strong':
            return 'bold';
        default:
            return 'normal';
    }
});

const gap = computed<string>(() => {
    switch (String(size)) {
        case '1':
        case '2':
            return '7';
        case '3':
            return '5';
        default:
            return '3';
    }
});

const imageSize = computed<string>(() => {
    switch (String(size)) {
        case '1':
            return '112px';
        case '2':
            return '96px';
        case '3':
            return '80px';
        case '4':
            return '64px';
        case '5':
            return '56px';
        case '6':
            return '48px';
        default:
            return '64px';
    }
});
</script>

<template>
    <SStack
        vertical
        align-items="center"
        justify-content="center"
        :gap="gap"
    >
        <img
            v-if="image != null"
            :src="image.url"
            :alt="image.alt"
            class="empty-state-image mb-3"
            :style="{ width: image.width ?? imageSize, height: image.height ?? imageSize }"
        />
        <SText
            is="p"
            :size="size"
            :variant="emphasis === 'subtle' ? 'secondary' : undefined"
            :weight="weight"
            class="empty-state-text d-block text-center"
        >
            {{ text }}
        </SText>
        <SStack
            v-if="$slots.actions"
            gap="5"
            justify-content="center"
            align-items="center"
        >
            <slot name="actions"></slot>
        </SStack>
    </SStack>
</template>

<style lang="scss" scoped>
    .empty-state-image {
        /* Image sizing is now controlled by the imageSize computed property */
        object-fit: contain;
    }

    .empty-state-text {
        max-width: 50ch;
    }
</style>
